/**
 * Country utilities for consistent country handling across the application
 * Based on the legacy BuyerFormCountryDropdown implementation
 */

import countries from './countries.json';

export interface CountryOption {
  /** Country code/key for form values */
  key: string;
  /** Display name for the country */
  content: string;
  /** Alternative display label */
  label: string;
}

/**
 * Get all countries as a simple array
 */
export const getCountries = (): string[] => {
  return countries;
};

/**
 * Get countries formatted as dropdown options
 * Compatible with both HeroUI Select and legacy Dropdown components
 */
export const getCountryOptions = (): CountryOption[] => {
  return countries.map((country) => ({
    key: country,
    content: country,
    label: country,
  }));
};

/**
 * Find a country by partial name match (case-insensitive)
 */
export const findCountryByName = (searchTerm: string): string[] => {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return countries;

  return countries.filter((country) => country.toLowerCase().includes(term));
};

/**
 * Validate if a country name exists in the list
 */
export const isValidCountry = (countryName: string): boolean => {
  return countries.includes(countryName);
};

/**
 * Get country search configuration for dropdowns
 */
export const getCountrySearchConfig = () => ({
  placeholder: 'Search country',
  noResultText: 'No results found',
});

/**
 * Format country name for consistent display
 */
export const formatCountryName = (countryName: string): string => {
  return countryName.trim();
};

/**
 * Get countries starting with a specific letter
 */
export const getCountriesByLetter = (letter: string): string[] => {
  const upperLetter = letter.toUpperCase();
  return countries.filter(
    (country) => country.charAt(0).toUpperCase() === upperLetter
  );
};

/**
 * Get total number of countries available
 */
export const getCountryCount = (): number => {
  return countries.length;
};
